import React from 'react'
import palette from 'core-system/Themes/palette'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import { Bike } from 'redux/bikeCatalog/bikeCatalogTypes'
import * as MicromobilityIcons from 'core-system/Icons/Micromobility'
import moment from 'moment'
import { formatVendorName } from './shared/components/NonDirectCheckoutHelper'

export const getProgramDisplayName = (program: BaseProgram) => {
  const superName = (program.superProgramName ?? '').trim()
  const name = (program.name ?? '').trim()
  return superName || name || 'Micromobility'
}

export const getResetsInLabel = (cycleEnd: string): string => {
  const days = Math.max(0, moment(cycleEnd).diff(moment(), 'days'))
  return `Resets in ${days} ${days === 1 ? 'day' : 'days'}`
}

export const formatBikePrice = (monthlyPrice: number | null | undefined): string => {
  if (monthlyPrice === null || monthlyPrice === undefined) {
    return 'Price unavailable'
  }
  return `$${(monthlyPrice / 100).toFixed(2)}/mo`
}

export const BIKE_PRICE_TAX_DISCLAIMER =
  'Amount shown is before taxes. The vendor will apply tax at checkout.'

/** Employee out-of-pocket after employer program budget is applied (cents). */
export const getPostSubsidyAmountCents = (
  monthlyPriceCents: number | null | undefined,
  subsidyBudgetCents: number
): number | null => {
  if (monthlyPriceCents === null || monthlyPriceCents === undefined) {
    return null
  }
  return Math.max(0, monthlyPriceCents - subsidyBudgetCents)
}

export const formatPostSubsidyPrice = (
  monthlyPriceCents: number | null | undefined,
  subsidyBudgetCents: number
): string => {
  const postSubsidyCents = getPostSubsidyAmountCents(
    monthlyPriceCents,
    subsidyBudgetCents
  )
  if (postSubsidyCents === null) {
    return 'Post-subsidy unavailable'
  }
  return `$${(postSubsidyCents / 100).toFixed(2)}/mo after subsidy`
}

export type BikeVendorFilter = 'all' | string

export const getVendorCounts = (
  bikes: Bike[] | null
): Record<string, number> => {
  const counts: Record<string, number> = {
    all: bikes?.length || 0,
  }
  if (bikes) {
    bikes.forEach((bike) => {
      const vendor = (bike.vendor || '').trim()
      if (!vendor) {
        return
      }
      counts[vendor] = (counts[vendor] || 0) + 1
    })
  }
  return counts
}

export const getVendorFilters = (bikes: Bike[] | null): BikeVendorFilter[] => {
  const counts = getVendorCounts(bikes)
  const vendors = Object.keys(counts)
    .filter((key) => key !== 'all')
    .sort((a, b) => formatVendorName(a).localeCompare(formatVendorName(b)))
  return ['all', ...vendors]
}

export const getFilteredBikes = (
  bikes: Bike[] | null,
  selectedVendor: BikeVendorFilter
): Bike[] => {
  if (!bikes) return []
  const sorted = [...bikes].sort((a, b) => {
    const availabilityCompare = Number(b.isAvailable !== false) - Number(a.isAvailable !== false)
    if (availabilityCompare !== 0) {
      return availabilityCompare
    }
    const vendorCompare = formatVendorName(a.vendor).localeCompare(
      formatVendorName(b.vendor)
    )
    if (vendorCompare !== 0) {
      return vendorCompare
    }
    return (a.name || '').localeCompare(b.name || '')
  })
  if (selectedVendor === 'all') return sorted
  return sorted.filter((bike) => bike.vendor === selectedVendor)
}

export type MicromobilityCategory = {
  key: string
  label: string
  description: string
  icon: React.ReactNode
}

export const getMicromobilityCategories = (
  program: BaseProgram
): MicromobilityCategory[] => {
  const categories: MicromobilityCategory[] = []

  if (program.shared) {
    categories.push({
      key: 'shared',
      label: 'Shared',
      description: 'Rent micromobility vehicles on a per-trip basis.',
      icon: React.createElement(MicromobilityIcons.SharedMicromobility, {
        width: 16,
        height: 16,
        color: palette.text.secondary,
      }),
    })
  }

  if (program.leasing) {
    categories.push({
      key: 'leasing',
      label: 'Leasing',
      description:
        'Get a monthly membership to lease a micromobility vehicle of your choice.',
      icon: React.createElement(MicromobilityIcons.Leasing, {
        width: 16,
        height: 16,
        color: palette.text.secondary,
      }),
    })
  }

  if (program.budgetCycle === 'YEARLY') {
    categories.push({
      key: 'yearlyPool',
      label: 'One-time purchase',
      description: 'One-time purchase option (uses a yearly pool).',
      icon: React.createElement(MicromobilityIcons.OneTimePurchase, {
        width: 16,
        height: 16,
        color: palette.text.secondary,
      }),
    })
  }

  if (categories.length === 0) {
    categories.push({
      key: 'spend',
      label: 'Micromobility spend',
      description: 'Use this budget on eligible micromobility providers.',
      icon: React.createElement(MicromobilityIcons.SharedMicromobility, {
        width: 16,
        height: 16,
        color: palette.text.secondary,
      }),
    })
  }

  return categories
}
